import { useCreateTaxonomy } from '@/hooks/api/taxonomy/useCreateTaxonomy';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

export const CreateTaxonomyModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { createTaxonomy, isCreating, createError } = useCreateTaxonomy();
  const { isTaxonomyAiClassificationEnabled } = useFeatureFlags();
  const [name, setName] = useState('');
  const [autoLabelOptedIn, setAutoLabelOptedIn] = useState(false);

  const handleSubmit = () => {
    createTaxonomy(
      { name: name.trim() || undefined, autoLabelOptedIn },
      {
        onSuccess: () => {
          setName('');
          setAutoLabelOptedIn(false);
          onClose();
        },
      }
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isCreating) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setName('');
      setAutoLabelOptedIn(false);
      onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            width: '405px',
          },
        },
      }}
    >
      <DialogTitle>Create Taxonomy</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          '&:first-of-type': { pt: 3 },
        }}
      >
        {createError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {createError.message}
          </Alert>
        )}
        <TextField
          label='Name'
          placeholder='Taxonomy'
          value={name}
          onChange={event => setName(event.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          autoFocus
          error={!!createError}
        />
        {isTaxonomyAiClassificationEnabled && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoLabelOptedIn}
                  onChange={event => setAutoLabelOptedIn(event.target.checked)}
                  disabled={isCreating}
                  size='small'
                  data-testid='create-taxonomy-auto-label-opt-in'
                />
              }
              label={<Typography variant='body2'>Automatically label new assets as they&apos;re uploaded</Typography>}
            />
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', ml: 4, mt: -0.5 }}>
              You can change this later, or run a one-time auto-label from the taxonomy page.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSubmit} disabled={isCreating} sx={{ width: '100px' }}>
          {isCreating ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
